import SummaryReportsAPI from 'dashboard/api/summaryReports';
import camelcaseKeys from 'camelcase-keys';

const typeMap = {
  inbox: {
    flagKey: 'isFetchingInboxSummaryReports',
    apiMethod: 'getInboxReports',
    mutationKey: 'setInboxSummaryReport',
    comparisonsKey: 'setInboxSummaryComparisons',
  },
  agent: {
    flagKey: 'isFetchingAgentSummaryReports',
    apiMethod: 'getAgentReports',
    mutationKey: 'setAgentSummaryReport',
    comparisonsKey: 'setAgentSummaryComparisons',
  },
  team: {
    flagKey: 'isFetchingTeamSummaryReports',
    apiMethod: 'getTeamReports',
    mutationKey: 'setTeamSummaryReport',
    comparisonsKey: 'setTeamSummaryComparisons',
  },
  label: {
    flagKey: 'isFetchingLabelSummaryReports',
    apiMethod: 'getLabelReports',
    mutationKey: 'setLabelSummaryReport',
    comparisonsKey: 'setLabelSummaryComparisons',
  },
  account: {
    flagKey: 'isFetchingAccountOverviewReports',
    apiMethod: 'getOverviewReports',
    mutationKey: 'setAccountOverviewReport',
    comparisonsKey: 'setAccountOverviewComparisons',
  },
  chats: {
    flagKey: 'isFetchingChatsReports',
    apiMethod: 'getChatsReports',
    mutationKey: 'setChatsReport',
    comparisonsKey: 'setChatsComparisons',
  },
};

async function fetchSummaryReports(type, params, { commit }) {
  const config = typeMap[type];
  if (!config) return;

  let error = null;
  try {
    commit('setUIFlags', { [config.flagKey]: true });
    const response = await SummaryReportsAPI[config.apiMethod](params);
    if (type === 'label') {
      const body = response.data || {};
      const reports = body.reports ?? body;
      commit(config.mutationKey, camelcaseKeys(reports, { deep: true }));
      commit('setLabelSummaryAggregates', {
        withoutTagsCount: body.without_tags_count || 0,
        withoutTagsReport: body.without_tags_report
          ? camelcaseKeys(body.without_tags_report, { deep: true })
          : null,
      });
    } else if (type === 'account') {
      commit(
        config.mutationKey,
        camelcaseKeys(response.data || {}, { deep: true })
      );
    } else {
      commit(config.mutationKey, camelcaseKeys(response.data, { deep: true }));
    }

    const periods = params.comparisonPeriods || [];
    if (periods.length) {
      const comparisonResponses = await Promise.all(
        periods.map(p =>
          SummaryReportsAPI[config.apiMethod]({
            since: p.from,
            until: p.to,
            businessHours: params.businessHours,
            labelIds: params.labelIds,
            excludeLabelIds: params.excludeLabelIds,
            inboxIds: params.inboxIds,
            teamIds: params.teamIds,
            agentIds: params.agentIds,
            channelTypes: params.channelTypes,
            episodeResult: params.episodeResult,
            receiptType: params.receiptType,
            tagPresence: params.tagPresence,
            resolutionMethod: params.resolutionMethod,
            statusAtPeriodEnd: params.statusAtPeriodEnd,
            reopening: params.reopening,
            transfers: params.transfers,
            humanResponse: params.humanResponse,
          })
        )
      );
      commit(
        config.comparisonsKey,
        comparisonResponses.map((r, i) => ({
          from: periods[i].from,
          to: periods[i].to,
          data: camelcaseKeys(
            type === 'label' ? (r.data?.reports ?? r.data) : r.data,
            { deep: true }
          ),
        }))
      );
    } else {
      commit(config.comparisonsKey, []);
    }
  } catch (e) {
    error = e;
  } finally {
    commit('setUIFlags', { [config.flagKey]: false });
  }
  if (error) throw error;
}

export const initialState = {
  inboxSummaryReports: [],
  agentSummaryReports: [],
  teamSummaryReports: [],
  labelSummaryReports: [],
  labelSummaryAggregates: {
    withoutTagsCount: 0,
    withoutTagsReport: null,
  },
  inboxSummaryComparisons: [],
  agentSummaryComparisons: [],
  teamSummaryComparisons: [],
  labelSummaryComparisons: [],
  accountOverviewReport: {},
  accountOverviewComparisons: [],
  chatsReport: {},
  chatsComparisons: [],
  chatsTimeSummary: {},
  uiFlags: {
    isFetchingInboxSummaryReports: false,
    isFetchingAgentSummaryReports: false,
    isFetchingTeamSummaryReports: false,
    isFetchingLabelSummaryReports: false,
    isFetchingAccountOverviewReports: false,
    isFetchingChatsReports: false,
    isFetchingChatsTimeSummary: false,
  },
};

export const getters = {
  getInboxSummaryReports(state) {
    return state.inboxSummaryReports;
  },
  getAgentSummaryReports(state) {
    return state.agentSummaryReports;
  },
  getTeamSummaryReports(state) {
    return state.teamSummaryReports;
  },
  getLabelSummaryReports(state) {
    return state.labelSummaryReports;
  },
  getLabelSummaryAggregates(state) {
    return state.labelSummaryAggregates;
  },
  getInboxSummaryComparisons(state) {
    return state.inboxSummaryComparisons;
  },
  getAgentSummaryComparisons(state) {
    return state.agentSummaryComparisons;
  },
  getTeamSummaryComparisons(state) {
    return state.teamSummaryComparisons;
  },
  getLabelSummaryComparisons(state) {
    return state.labelSummaryComparisons;
  },
  getAccountOverviewReport(state) {
    return state.accountOverviewReport;
  },
  getAccountOverviewComparisons(state) {
    return state.accountOverviewComparisons;
  },
  getChatsReport(state) {
    return state.chatsReport;
  },
  getChatsComparisons(state) {
    return state.chatsComparisons;
  },
  getChatsTimeSummary(state) {
    return state.chatsTimeSummary;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  fetchInboxSummaryReports({ commit }, params) {
    return fetchSummaryReports('inbox', params, { commit });
  },

  fetchAgentSummaryReports({ commit }, params) {
    return fetchSummaryReports('agent', params, { commit });
  },

  fetchTeamSummaryReports({ commit }, params) {
    return fetchSummaryReports('team', params, { commit });
  },

  fetchLabelSummaryReports({ commit }, params) {
    return fetchSummaryReports('label', params, { commit });
  },

  fetchAccountOverviewReports({ commit }, params) {
    return fetchSummaryReports('account', params, { commit });
  },

  fetchChatsReports({ commit }, params) {
    return fetchSummaryReports('chats', params, { commit });
  },

  async fetchChatsTimeSummary({ commit }, params) {
    try {
      commit('setUIFlags', { isFetchingChatsTimeSummary: true });
      const response = await SummaryReportsAPI.getChatsTimeSummary(params);
      commit(
        'setChatsTimeSummary',
        camelcaseKeys(response.data || {}, { deep: true })
      );
    } finally {
      commit('setUIFlags', { isFetchingChatsTimeSummary: false });
    }
  },
};

export const mutations = {
  setInboxSummaryReport(state, data) {
    state.inboxSummaryReports = data;
  },
  setAgentSummaryReport(state, data) {
    state.agentSummaryReports = data;
  },
  setTeamSummaryReport(state, data) {
    state.teamSummaryReports = data;
  },
  setLabelSummaryReport(state, data) {
    state.labelSummaryReports = data;
  },
  setLabelSummaryAggregates(state, data) {
    state.labelSummaryAggregates = { ...state.labelSummaryAggregates, ...data };
  },
  setInboxSummaryComparisons(state, data) {
    state.inboxSummaryComparisons = data;
  },
  setAgentSummaryComparisons(state, data) {
    state.agentSummaryComparisons = data;
  },
  setTeamSummaryComparisons(state, data) {
    state.teamSummaryComparisons = data;
  },
  setLabelSummaryComparisons(state, data) {
    state.labelSummaryComparisons = data;
  },
  setAccountOverviewReport(state, data) {
    state.accountOverviewReport = data;
  },
  setAccountOverviewComparisons(state, data) {
    state.accountOverviewComparisons = data;
  },
  setChatsReport(state, data) {
    state.chatsReport = data;
  },
  setChatsComparisons(state, data) {
    state.chatsComparisons = data;
  },
  setChatsTimeSummary(state, data) {
    state.chatsTimeSummary = data;
  },
  setUIFlags(state, uiFlag) {
    state.uiFlags = { ...state.uiFlags, ...uiFlag };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
